import React, {
  useEffect,
  useState,
  useCallback,
} from "react";
import { MapTo } from "@adobe/aem-react-editable-components";
import { useInView } from "react-intersection-observer";
import { useAnalytics } from "@lexus.com/ui/dist/components/analytics";
import { useConfig } from "@lexus.com/ui/dist/_nextgen/packages/config";

import {
  PersonalizationContextBindingProvider,
} from "@lexus.com/ui/dist/packages/personalization";
import { withModuleProps } from '@lexus.com/ui/dist/packages/ModuleProps';
import { DynamicHeadingLevelProvider } from '@lexus.com/ui/dist/components/DynamicHeading';
import ComponentContainer from '@lexus.com/ui/dist/_nextgen/components/ComponentContainer';

const EXCLUDE_ANALYTICS = ["OverlayComponent", "ContainerComponent"];

const Analytics = ({ children, analytics }) => {
  const [delay, setDelay] = useState(true);

  useEffect(() => {
    setTimeout(() => setDelay(false), 1000);
  }, []);

  const onView = ({ _component, ...props }) => {
    if (!EXCLUDE_ANALYTICS.includes(_component)) {
      fireAnalytics(`${_component}/view`, props);
    }
  }

  const [analyticsRef] = useInView({
    triggerOnce: true,
    rootMargin: "-5%",
    threshold: 0,
    skip: delay,
    onChange: (inView, entry) => {
      if (inView) {
        onView(analytics);
      }
    },
  });

  const setRefs = useCallback(
    (node) => {
      analyticsRef(node);
    },
    [analyticsRef]
  );
  const fireAnalytics = useAnalytics("");



  return (
    <div ref={setRefs}>
      {children}
    </div>
  );
}

const AnimateInView = ({ children, ...props }) => {
  const { enableAnimation } = useConfig();

  const [inviewRef, inView, entry] = useInView({
    triggerOnce: true,
    threshold: 0,
    skip: !enableAnimation,
  });
  const setRefs = useCallback(
    (node) => {
      inviewRef(node);
    },
    [inviewRef]
  );

  useEffect(() => {
    if (inView) {
      entry.target.classList.add("lcom__load-animation--active");
    }
  }, [inView]);

  // This scrolls to the correct section when a user lands on a page with /models/[model-name]/[section-name] in the url
  useEffect(() => {
    const { pathname } = window.location;
    if (pathname.indexOf("/models/") !== -1) {
      const segments = pathname.split("/");

      if (segments[3]) {
        const id = `model_${segments[3]}`;
        let section = document.getElementById(`${id}`);
        if (section) {
          setTimeout(() => {
            //section.scrollIntoView(); // Todo: For some reason scrollIntoView doesn't work in chrome. We can get offset and manually scroll the window instead
            window.scrollTo(0, section.offsetTop);
          }, 100);
        }
      }
    }
  }, []);

  return (
    <div
      ref={setRefs}
      className={enableAnimation ? "lcom__load-animation" : ""}
    >
      {children}
    </div>
  );
};

// Todo: we want to merge the configs and transforms into a common integration at some point, since 99% of the integrations are exactly the same
const withAemIntegration = (configString) => (Component, config) => {
  // const config = {
  //   emptyLabel: configString.split("/").at(-1),
  //   isEmpty: function (props) {
  //     return !props || !props.title || props.title.trim().length < 1;
  //   },
  // };
  const WrappedComponent = withModuleProps(Component);
  const AemComponent = (aemProps) => {
    const _component = Component?.displayName || Component.name;
    const { componentCommon = {}, componentDesign, ...props } = aemProps;
    const { heading, headingColor, subHeading, subHeadingColor } = componentCommon;
    const analyticsData = {
      _component,
      ...props,
    };
    return (
        <AnimateInView {...props}>
          <PersonalizationContextBindingProvider {...props}>
            <Analytics analytics={analyticsData}>
              <DynamicHeadingLevelProvider>
                  <ComponentContainer
                    componentDesign={componentDesign}
                    theme={aemProps.theme}
                    heading={heading}
                    headingColor={headingColor}
                    subHeading={subHeading}
                    subHeadingColor={subHeadingColor}
                    className={componentDesign?.classnames}
                    id={props.id ? `${props.id}` : undefined}
                  >
                    <WrappedComponent 
                      {...props} 
                    />
                  </ComponentContainer>
              </DynamicHeadingLevelProvider>
            </Analytics>
          </PersonalizationContextBindingProvider>
        </AnimateInView>

    );
  };

  return MapTo(configString)(AemComponent, config);
};

export default withAemIntegration;
